// Representación de una FICHA (instancia concreta en juego).
// Las definiciones vienen de data/tiles-es.json; aquí creamos instancias con id único.
import { uid } from '../utils/helpers.js';
import { TILE_TYPE, PLACEMENT } from '../utils/constants.js';

/**
 * Crea una instancia de ficha a partir de una definición del catálogo.
 * @param {object} def definición del JSON
 * @returns {object} instancia serializable
 */
export function createTileInstance(def) {
  return {
    instanceId: uid('t'),
    defId: def.id,
    text: def.text,
    type: def.type,
    value: def.value ?? 0,
    widthUnits: def.widthUnits ?? 1,
    placement: def.placement ?? PLACEMENT.ANY,
    canSplit: !!def.canSplit,
    splitInto: def.splitInto ? [...def.splitInto] : null,
    transforms: def.transforms ? [...def.transforms] : [],
    color: def.color,
    icon: def.icon,
    rarity: def.rarity,
    wildKind: def.wildKind || null,
    // Estado mutable en juego:
    assignedText: null, // para comodín universal: el texto que representa
  };
}

/** Texto efectivo de la ficha (comodín usa su valor asignado). */
export function effectiveText(tile) {
  if (tile.type === TILE_TYPE.WILDCARD && tile.wildKind === 'universal') {
    return tile.assignedText || '';
  }
  return tile.text;
}

// Valores base de letra para fichas sintéticas (resultado de dividir/fusionar).
const LETTER_VALUES = {
  a: 1, e: 1, o: 1, i: 1, u: 1, s: 1, r: 1, n: 1, l: 1, t: 1,
  d: 2, c: 2, m: 2, p: 2, g: 2, b: 3, v: 3, f: 3, h: 3,
  j: 4, y: 4, z: 4, q: 5, x: 6, 'ñ': 6,
};
const LETTER_COLOR = '#e8dcc0';
const FRAGMENT_COLOR = '#3fa34d';

/** Crea una ficha de LETRA temporal para cualquier carácter (incluye tildes/Ñ). */
export function makeLetterTile(ch) {
  const key = ch.toLowerCase();
  return {
    instanceId: uid('t'),
    defId: `synthetic-letter-${key}`,
    text: ch,
    type: TILE_TYPE.LETTER,
    value: LETTER_VALUES[key] ?? 1,
    widthUnits: 1,
    placement: PLACEMENT.ANY,
    canSplit: false,
    splitInto: null,
    transforms: [],
    color: LETTER_COLOR,
    icon: 'circle',
    rarity: 'common',
    wildKind: null,
    assignedText: null,
    synthetic: true,
  };
}

/** Crea una ficha de FRAGMENTO fusionado a partir de un texto (p.ej. C + IÓN -> CIÓN). */
export function makeFragmentTile(text, { value } = {}) {
  const letters = Array.from(text);
  return {
    instanceId: uid('t'),
    defId: `synthetic-fragment-${text.toLowerCase()}`,
    text,
    type: TILE_TYPE.SYLLABLE,
    value: value ?? Math.max(2, letters.length),
    widthUnits: Math.min(4, Math.max(1, letters.length - 1)),
    placement: PLACEMENT.ANY,
    canSplit: true,
    splitInto: letters,
    transforms: [],
    color: FRAGMENT_COLOR,
    icon: 'square',
    rarity: 'special',
    wildKind: null,
    assignedText: null,
    synthetic: true,
  };
}

export function isPrefix(tile) {
  return tile.type === TILE_TYPE.PREFIX;
}
export function isSuffix(tile) {
  return tile.type === TILE_TYPE.SUFFIX;
}
export function isWildcard(tile) {
  return tile.type === TILE_TYPE.WILDCARD;
}
